//* Services imports
import { api } from "../../services/apis/configs/axiosConfigs";

//* Utils imports
import { API_URI } from "../../constants/consts";

//* Types imports
import { type Pagination } from "../../services/stores/types";

//* Importing types
import {
  type CoursesDetails,
  type CourseResponse,
  type Course,
} from "./types/type";

export const courseAPI = {
  create: async function (
    data: { name: string; description: string },
    token: string,
    cancel = false
  ): Promise<Course> {
    return api
      .request({
        url: `${API_URI}/admin/private/courses`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data,
      })
      .then((response) => response.data.data)
      .catch((error) => {
        console.error("Error creating unit:", error);
        throw new Error("Failed to create unit");
      });
  },

  getAll: async function (
    search: string = "",
    page: number = 1,
    token: string,
    perPage: number = 10
  ): Promise<CourseResponse> {
    const searchQuery = search ? `&search=${search}` : "";
    const url = `${API_URI}/admin/private/courses?page=${page}&perPage=${perPage}${searchQuery}`;

    return api
      .request({
        url,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        const { data } = response.data;

        const pagination: Pagination = data.pagination
          ? {
              currentPage: data.pagination.currentPage,
              totalPages: data.pagination.totalPages,
              itemsPerPage: data.pagination.itemsPerPage,
              totalItems: data.pagination.totalItems,
            }
          : {
              currentPage: 0,
              totalPages: 0,
              itemsPerPage: 0,
              totalItems: 0,
            };

        return {
          courses: data.courses || [],
          pagination,
        };
      })
      .catch((error) => {
        console.error("Error fetching students:", error);
        throw new Error("Failed to fetch students");
      });
  },

  getById: async function (
    id: string,
    token: string,
    options: {
      searchTermStudents?: string;
      studentFilter?: "all" | "active" | "inactive";
      pageStudents?: number;
      perPageStudents?: number;
      searchTermTeams?: string;
      pageTeams?: number;
      perPageTeams?: number;
    } = {},
    cancel = false
  ): Promise<CoursesDetails> {
    const {
      searchTermStudents,
      studentFilter,
      pageStudents,
      perPageStudents,
      searchTermTeams,
      pageTeams,
      perPageTeams,
    } = options;

    const params = new URLSearchParams();

    if (searchTermStudents)
      params.append("searchTermStudents", searchTermStudents);
    if (studentFilter) params.append("studentFilter", studentFilter);
    if (pageStudents) params.append("pageStudents", pageStudents.toString());
    if (perPageStudents)
      params.append("perPageStudents", perPageStudents.toString());
    if (searchTermTeams) params.append("searchTermTeams", searchTermTeams);
    if (pageTeams) params.append("pageTeams", pageTeams.toString());
    if (perPageTeams) params.append("perPageTeams", perPageTeams.toString());

    return api
      .request({
        url: `${API_URI}/admin/private/courses/${id}?${params.toString()}`,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        if (response.data && response.data.result) {
          const data = response.data.data;

          // Transformar as propriedades de paginação para camelCase
          if (data.teams && data.teams.pagination) {
            const { current_page, per_page, total_pages } =
              data.teams.pagination;
            data.teams.pagination = {
              currentPage: current_page,
              perPage: per_page,
              totalPages: total_pages,
            };
          }

          if (data.students && data.students.pagination) {
            const { current_page, per_page, total_pages } =
              data.students.pagination;
            data.students.pagination = {
              currentPage: current_page,
              perPage: per_page,
              totalPages: total_pages,
            };
          }

          return data; // Retorna os dados com as propriedades ajustadas
        } else {
          throw new Error("Unit not found or invalid response.");
        }
      })
      .catch((error) => {
        console.error("Error fetching unit:", error); // Log for debugging
        throw new Error("Failed to fetch unit");
      });
  },

  update: async function (
    id: string,
    data: { name: string; description: string },
    token: string
  ): Promise<Course> {
    return api
      .request({
        url: `${API_URI}/admin/private/courses/${id}`,
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data,
      })
      .then((response) => response.data.data)
      .catch((error) => {
        console.error("Error updating course:", error);
        throw new Error("Failed to update course");
      });
  },

  delete: async function (id: string, token: string): Promise<void> {
    return api
      .request({
        url: `${API_URI}/admin/private/courses/${id}`,
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {})
      .catch((error) => {
        console.error("Error deleting course:", error);
        throw new Error("Failed to delete course");
      });
  },
};
